// ----------------------------------------------------------------------- imports externos
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { supabase } from "../supabase/SupabaseClient";
import Loading from "./Loading";
import Marca from "../components/Marca";
import CampoClave from "../components/funcionales/CampoClave";

/* Pantalla donde cae quien toca el enlace del correo.
 *
 * Cómo llega la sesión: Supabase manda los tokens en el fragmento de la URL
 * (#access_token=...) y el cliente los levanta solo, sin que haya que leer
 * nada a mano. Hasta que eso pasa NO hay sesión, así que la pantalla arranca
 * verificando y no mostrando el formulario — si se muestra antes, quien tarde
 * dos segundos en escribir se come un error de "no autorizado" sin entender.
 *
 * El enlace es de un solo uso y vence. Un enlace vencido es la situación más
 * común y confusa de todo el flujo (alguien abre el correo al otro día), así
 * que tiene su propio mensaje y un camino de salida, en vez de un error suelto.
 */

const SEGUNDOS_PARA_REDIRIGIR = 5;

const CrearClave = () => {
  const navigate = useNavigate();

  const [estado, setEstado] = useState("verificando"); // verificando | formulario | sin_sesion | listo
  const [clave, setClave] = useState("");
  const [repetir, setRepetir] = useState("");
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [restante, setRestante] = useState(SEGUNDOS_PARA_REDIRIGIR);

  // ── ¿hay sesión de recuperación? ────────────────────────────────────────
  useEffect(() => {
    let vigente = true;

    // El evento PASSWORD_RECOVERY llega cuando el cliente terminó de procesar
    // el fragmento de la URL. Escucharlo evita competir contra ese proceso.
    const { data: sub } = supabase.auth.onAuthStateChange((evento, sesion) => {
      if (!vigente) return;
      if (evento === "PASSWORD_RECOVERY" || sesion) setEstado("formulario");
    });

    /* Por si el evento ya pasó antes de montar este componente.
     *
     * NO PISA EL «formulario» QUE YA PUSO EL EVENTO. Antes escribía el estado
     * sin mirar, y como las dos cosas corren en paralelo el orden decidía la
     * pantalla: si `getSession()` resolvía primero —con la sesión todavía sin
     * crear— dejaba «Enlace no válido» encima de una sesión que existía medio
     * segundo después. El mismo enlace andaba o no andaba según cuál ganara. */
    supabase.auth.getSession().then(({ data }) => {
      if (!vigente) return;
      setEstado((previo) =>
        previo === "formulario" ? previo : data?.session ? "formulario" : "sin_sesion",
      );
    });

    return () => {
      vigente = false;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  // ── cuenta regresiva, sólo cuando ya está creada ────────────────────────
  useEffect(() => {
    if (estado !== "listo") return;
    if (restante <= 0) {
      irAlLogin();
      return;
    }
    const t = setTimeout(() => setRestante((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, restante]);

  const irAlLogin = async () => {
    // Se cierra la sesión de recuperación a propósito: la idea es que entren
    // con la clave nueva y comprueben en el momento que funciona. Si quedaran
    // adentro sin usarla, se enterarían recién la próxima vez, lejos de acá.
    await supabase.auth.signOut();
    navigate("/login", { replace: true });
  };

  const problemaDeClave = useMemo(() => {
    if (clave.length > 0 && clave.length < 8)
      return "La contraseña tiene que tener al menos 8 caracteres.";
    if (repetir.length > 0 && clave !== repetir)
      return "Las dos contraseñas no coinciden.";
    return "";
  }, [clave, repetir]);

  const puedeGuardar = clave.length >= 8 && clave === repetir && !guardando;

  const guardar = async (e) => {
    e.preventDefault();
    if (!puedeGuardar) return;

    setGuardando(true);
    setError("");
    const { error: err } = await supabase.auth.updateUser({
      password: clave,
      // Apaga la bandera de "tiene que cambiarla", que es lo que mira el resto
      // de la app para forzar el cambio.
      data: { must_reset_password: false },
    });
    setGuardando(false);

    if (err) {
      /* «NO PODÉS» Y «NO PUDE» NO SON LO MISMO, y acá salían iguales.
       *
       * Cualquier falla —Supabase lento, la clave rechazada por la política,
       * la red— contestaba «el enlace venció o ya se usó», que es una causa
       * concreta y casi siempre la equivocada. El que la lee va a pedir un
       * enlace nuevo, que no arregla nada de lo anterior, y cuando el segundo
       * falle igual va a concluir que el sistema está roto.
       *
       * El enlace vencido tiene un código propio y sólo ése se dice así. Es la
       * misma distinción que ya está escrita en el borrado de tickets. */
      const vencido =
        err.status === 401 ||
        err.status === 403 ||
        /expired|invalid|jwt|session|token/i.test(err.message || "");
      setError(
        vencido
          ? "No se pudo guardar: el enlace venció o ya se había usado. Son de un solo uso y duran una hora — pedí uno nuevo desde la pantalla de ingreso."
          : `No se pudo guardar la contraseña. ${err.message || "El servidor no contestó."} Probá de nuevo; si sigue igual, el enlace todavía sirve.`,
      );
      return;
    }
    setEstado("listo");
  };

  const encabezado = (
    <Marca />
    );

  return (
    <div className="login-screen">
      {encabezado}
      <div className="login-screen__panel">
        <div className="login-card">
          {estado === "verificando" && <Loading />}

          {estado === "sin_sesion" && (
            <>
              <h2>Enlace no válido</h2>
              <p className="login-card__texto">
                Este enlace ya se usó o venció. Por seguridad son de un solo uso
                y duran una hora.
              </p>
              <p className="login-card__texto login-card__texto--menor">
                Volvé a la pantalla de ingreso, escribí tu correo y te enviamos
                uno nuevo.
              </p>
              <button
                type="button"
                className="btn btn--primary btn--block"
                onClick={() => navigate("/login", { replace: true })}
              >
                Ir al ingreso
              </button>
            </>
          )}

          {estado === "formulario" && (
            <>
              <h2>Creá tu contraseña</h2>
              <p className="login-card__subtitle">
                Va a ser la que uses de ahora en adelante para ingresar.
              </p>
              <form onSubmit={guardar}>
                <label className="field-label" htmlFor="clave-nueva">
                  Contraseña
                </label>
                <CampoClave
                  id="clave-nueva"
                  placeholder="Al menos 8 caracteres"
                  value={clave}
                  onChange={(e) => setClave(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  required
                />
                <label className="field-label" htmlFor="clave-repetida">
                  Repetir contraseña
                </label>
                <CampoClave
                  id="clave-repetida"
                  placeholder="••••••••"
                  value={repetir}
                  onChange={(e) => setRepetir(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                {(problemaDeClave || error) && (
                  <p className="login-card__error">{problemaDeClave || error}</p>
                )}
                <button
                  type="submit"
                  className="btn btn--primary btn--block"
                  disabled={!puedeGuardar}
                >
                  {guardando ? "Guardando…" : "Crear contraseña"}
                </button>
              </form>
            </>
          )}

          {estado === "listo" && (
            <>
              <h2>Contraseña creada</h2>
              <p className="login-card__texto">
                Listo. Te llevamos a la pantalla de ingreso para que entres con
                tu contraseña nueva.
              </p>

              {/* La barra existe para que la espera se vea, no sólo se sienta.
                  Un cartel quieto durante cinco segundos parece colgado. */}
              <div
                className="cuenta-regresiva"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={SEGUNDOS_PARA_REDIRIGIR}
                aria-valuenow={SEGUNDOS_PARA_REDIRIGIR - restante}
                aria-label="Tiempo hasta la redirección"
              >
                <div
                  className="cuenta-regresiva__barra"
                  style={{
                    width: `${((SEGUNDOS_PARA_REDIRIGIR - restante) / SEGUNDOS_PARA_REDIRIGIR) * 100}%`,
                  }}
                />
              </div>
              <p className="login-card__texto login-card__texto--menor">
                Te redirigimos en {restante}{" "}
                {restante === 1 ? "segundo" : "segundos"}.
              </p>

              <button
                type="button"
                className="btn btn--primary btn--block"
                onClick={irAlLogin}
              >
                Redirigirme ahora
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrearClave;
